<script lang="ts">
  import { _ } from "svelte-i18n";
  import Badge from "$lib/components//Badge/Badge.svelte";
  import Price from "$lib/components//Price/Price.svelte";
  import Typography from "$lib/components//Typography/Typography.svelte";
  import { ImageConfig3Card } from "$lib/components/Cards/ProductCard/ProductCard.image-config";
  import type {
    Image,
    SelectedOptionInput as Option,
    MoneyV2,
    ProductVariantEdge,
    ProductVariant,
  } from "$lib/gql/gen/codegen";
  import type { PriceRange } from "$lib/gql/genCms/codegen";
  import { linkTo } from "$lib/utils/url";
  import Tray from "$lib/components/Tray/Tray.svelte";
  import { tray } from "$lib/stores/tray";
  import BackInStock from "$lib/components/BackInStock/BackInStock.svelte";
  import Icon from "$lib/components/Icon/Icon.svelte";
  import { page } from "$app/stores";
  import type { colourVariant } from "$lib/data/colours.data";
  import ProductOptionSelector from "$lib/components/ProductDetail/ProductOptionSelector/ProductOptionSelector.svelte";
  import { modals } from "$lib/stores/modal";
  import AddToBag from "$lib/components/AddToBag/AddToBag.svelte";
  import { cart } from "$lib/stores/cart";
  import { splitTrouserSizes } from "$lib/utils/splitTrouserSizes";
  import Modal from "$lib/components/Modal/Modal.svelte";
  import Button from "$lib/components/Button/Button.svelte";
  import { createEventDispatcher } from "svelte";
  import { fade } from "svelte/transition";
  import { RegionStore, regionStore } from "$lib/stores/region";
  import { stripBasePathAndLang } from "$lib/utils/url";
  import type { SizeOption } from "$lib/data/size.data";
  import { type SizeGuideCategory } from "$lib/utils/findSizeGuideCategory";
  import { minicart } from "$lib/stores/minicart";
  import ScrollGradient from "$lib/components/ScrollGradient/ScrollGradient.svelte";
  import ColourSwatch from "$lib/components/ColourSwatch/ColourSwatch/ColourSwatch.svelte";
  import GiftCard from "$lib/components/GiftCard/GiftCard.svelte";
  import Picture from "$lib/components/Picture/Picture.svelte";

  export let title: string;
  export let subtitle: string;
  export let priceRange: PriceRange;
  export let comparePriceRange: PriceRange;
  export let image: Image;
  export let url: string;
  export let showPrice = true;
  export let badge = "";
  export let paddingLg = false;
  export let imgBorder = false;
  export let productVariants: ProductVariantEdge[];
  export let productOptions: Option[];
  export let selectedColourOption: colourVariant = null;
  export let colourOptions: colourVariant[];
  export let sizeOptions: { value: string; inStock: boolean }[];
  export let quickBuy: boolean = false;
  export let id: string;
  export let index: number;
  export let openTray: { route: string; ids: string[] } = {
    route: "",
    ids: [],
  };
  export let imageConfig = ImageConfig3Card;
  export let price: MoneyV2;
  export let comparePrice: MoneyV2;

  export let smallThumb: boolean = false;

  export let closeSearch: () => void;

  type Variant = {
    id: string;
    title: string;
    sku?: string;
    price: MoneyV2;
    compareAtPrice?: MoneyV2;
    selectedOptions: Option[];
  };

  export const sizeGuide: SizeGuideCategory = {
    bigAndTall: false,
    category: $_("outwear & tops"),
    gender: "men",
  };

  const dispatch = createEventDispatcher();
  const regionInfo = regionStore();
  const resetQuickBuy = () => {
    outOfStock = false;
    selectedVariant = undefined;
    selectedSize = undefined;
    selectedWaistSize = undefined;
    selectedLegSize = undefined;
    backInStock = false;
    giftCard = false;
    giftCardEmail = "";
    giftCardName = "";
    giftCardMessage = "";
    giftCardDate = new Date().toISOString().split("T")[0];
    giftCardSendOnDate = false;

    autoSelectOneSize();
  };

  let viewportWidth: number;
  let isMobile = false;
  let lgWidth = 1280;
  let showTray = false;
  let modal: any;
  let imageObject = {
    ...image,
    url: image.originalSrc,
  };
  let trouserSizeOptions:
    | { waist: string; leg: string; value: string; inStock: boolean }[]
    | null;
  let waistSizeOptions: { value: string; inStock: boolean }[] | null;
  let legSizeOptions: { value: string; inStock: boolean }[] | null;
  let optionsSelected = [false];
  let selectedOptions: Option[];
  let selectedVariant: ProductVariantEdge;
  let selectedSize: SizeOption | string | undefined = undefined;
  let selectedWaistSize: string | undefined = undefined;
  let selectedLegSize: string | undefined = undefined;
  let selectedVariantInStock = false;
  let trouserSizeError: string | null = null;

  $: isMobile = viewportWidth < lgWidth;
  $: isOpen = false;
  $: outOfStock = false;
  $: $tray = openTray;
  $: showTray = openTray && openTray?.ids?.includes(`${id}-${index}`);
  $: backInStock = false;

  $: giftCard = false;

  let giftCardEmail = "";
  let giftCardName = "";
  let giftCardMessage = "";
  let giftCardDate = new Date().toISOString().split("T")[0];
  let giftCardSendOnDate = false;

  $: {
    ({ trouserSizeOptions, waistSizeOptions, legSizeOptions } =
      splitTrouserSizes(sizeOptions));
  }

  const autoSelectOneSize = () => {
    if (sizeOptions && sizeOptions.length !== 1) {
      return;
    }

    //size selection
    selectedSize = sizeOptions[0];
    trouserSizeError = null;
    optionSelector(
      selectedSize.value,
      selectedOptions.map((o: Option) => o.name).indexOf("size"),
    );

    //also need selected colour variant to make things work
    selectedOptions.filter(opt => opt.name === "colour")[0].value =
      selectedColourOption.identifier;
    optionsSelected[
      selectedOptions.map((o: Option) => o.name).indexOf("colour")
    ] = true;
  };

  // Force auto selection when there is only 1 size available
  $: if (sizeOptions && sizeOptions.length === 1) {
    autoSelectOneSize();
  }

  $: {
    if (trouserSizeOptions && selectedWaistSize) {
      legSizeOptions = legSizeOptions?.map(e => {
        return {
          ...e,
          inStock:
            trouserSizeOptions?.filter(
              o => o.leg === e.value && o.waist === selectedWaistSize,
            )[0]?.inStock || false,
        };
      });
    }
  }

  selectedOptions =
    productOptions &&
    productOptions.map(opt => ({
      name: opt.name,
      value: opt.value,
    }));

  $: if (selectedColourOption) {
    selectedOptions.filter(opt => opt.name === "colour")[0].value =
      selectedColourOption?.identifier;
    optionsSelected[1] = true;
  }

  $: if (productOptions) {
    optionsSelected = productOptions.map(() => false);
  }

  const optionSelector = (value: string, i: number) => {
    selectedOptions[i].value = value;
    optionsSelected[i] = true;
  };

  const waistOptionSelector = (value: string, i: number) => {
    selectedWaistSize = value;
    if (selectedLegSize) {
      selectedSize = trouserSizeOptions?.filter(
        o => o.waist === value && o.leg === selectedLegSize,
      )[0]?.value;

      if (selectedSize) {
        selectedOptions[i].value = selectedSize;
        optionsSelected[i] = true;
      }
    }
    trouserSizeError = null;
  };

  const legOptionSelector = (value: string, i: number) => {
    selectedLegSize = value;

    if (selectedWaistSize) {
      selectedSize = trouserSizeOptions?.filter(
        o => o.leg === value && o.waist === selectedWaistSize,
      )[0]?.value;

      if (selectedSize) {
        selectedOptions[i].value = selectedSize;
        optionsSelected[i] = true;
      }
    }
    trouserSizeError = null;
  };

  const handleSelectVariant = (
    variants: ProductVariantEdge[],
    options: Option[],
  ) => {
    selectedVariant = variants?.filter(
      v =>
        v.node.selectedOptions?.filter(o => o.name === "size")[0].value ===
          options?.filter(o => o.name === "size")[0].value &&
        v.node.selectedOptions?.filter(o => o.name === "colour")[0].value ===
          options?.filter(o => o.name === "colour")[0].value,
    )[0];
    return selectedVariant;
  };

  $: if (optionsSelected.every(item => item)) {
    selectedVariant = handleSelectVariant(productVariants, selectedOptions);
  }

  $: selectedVariantInStock =
    selectedVariant && selectedVariant?.node?.quantityAvailable > 0;

  $: if ($page?.url?.pathname !== openTray?.route) {
    openTray.ids = [];
    showTray = false;
  }

  const onAddToBasket = async (
    variant: Variant,
    giftCardEmail?: string,
    giftCardName?: string,
    giftCardMessage?: string,
    giftCardDate?: string,
    giftCardSendOnDate?: boolean,
  ) => {
    const variantId: string = variant?.id;
    const urlAttribute: string = stripBasePathAndLang(url);
    if (isGiftCard && !giftCard) {
      giftCard = true;
    } else if (selectedVariantInStock) {
      let cartOperationSuccessful = false;
      if ($cart) {
        try {
          await cart.cartLinesAdd(
            $cart?.id,
            variantId,
            urlAttribute,
            regionInfo,
            {
              recipientEmail: giftCardEmail,
              recipientName: giftCardName,
              message: giftCardMessage,
              ...(giftCardSendOnDate && { sendOn: giftCardDate }),
            },
          );
          cartOperationSuccessful = true;
        } catch (error) {
          console.error("Error adding item to cart:", error);
        }
      }
      if (!cartOperationSuccessful) {
        try {
          await cart.createCart(variantId, urlAttribute, {
            recipientEmail: giftCardEmail,
            recipientName: giftCardName,
            message: giftCardMessage,
            ...(giftCardSendOnDate && { sendOn: giftCardDate }),
          });
          cartOperationSuccessful = true;
        } catch (error) {
          console.error("Error creating cart:", error);
        }
      }
      if (cartOperationSuccessful) {
        minicart.open();
        resetQuickBuy();
        showTray = false;
      }
    } else {
      backInStock = true;
    }
  };

  $: isGiftCard = productVariants?.some(option =>
    option?.node?.sku?.includes("GIFT CARD"),
  );

  $: if (image) {
    imageObject = {
      ...image,
      url: image.originalSrc,
    };
  }
</script>

<style lang="scss">
  .product-card:hover .product-card--img:not(.tray-open) {
    transform: scale(1.08);
    transform-origin: center;
  }

  :global(.product-card--img-wrapper .product-card--img .inner.coverImage) {
    overflow: visible;
  }

  :global(.product-card--img-wrapper > .product-card--img > .inner) {
    aspect-ratio: 4/5;
  }

  .product-card {
    display: block;
    position: relative;

    &--anchor {
      position: static;
      text-decoration: none;

      /* make the anchor tag cover the entire product card area */
      &:before {
        content: "";
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 3;
      }
    }
    &--badge {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 6;
    }
    &--img {
      position: relative;
      aspect-ratio: 4/5;
      &-wrapper {
        aspect-ratio: 4/5;
        display: block;
        position: relative;
        overflow: hidden;

        &__border {
          border-bottom: 0.5px solid $background;
        }
      }
      transition: 300ms ease-in-out;
    }
    &--info {
      background-color: $surface;
      position: relative;
      padding: 8px;

      text-decoration: none;

      &__padded-lg {
        padding: 16px;
      }
      &__title {
        margin-bottom: 8px;
        text-transform: capitalize;
      }
      &__price {
        margin-top: 8px;
      }
    }

    &--quick-buy {
      &__container {
        position: absolute;
        z-index: 5;
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        // pointer-events: none;
        bottom: 0px;

        @include breakpoint(md) {
          max-height: 270px;
        }
        @include breakpoint(xl) {
          max-height: 340px;
        }
        @include breakpoint(xxl) {
          max-height: 400px;
        }
      }

      &__button {
        display: flex;
        justify-content: center;
        align-items: center;
        pointer-events: all;
        position: relative;
        width: 14px;
        height: 14px;
        padding: 20px;
        margin: 16px;
        background-color: $inverse-on-surface-variant-60;
        color: $on-background;
        border: 1px solid $on-surface-10;
        border-radius: 50%;
      }
    }

    &--add-to-bag {
      border-top: 1px solid $background;
      padding: 16px;

      @include retina {
        border-top: 0.5px solid $background;
      }

      @include breakpoint(md) {
        padding: 24px 44px;
      }
    }
  }

  .quick-buy-colours {
    padding-top: 16px;
  }

  .tray-wrapper {
    &--add-to-bag {
      padding: 24px 16px 16px;
    }

    :global(.option-container .option-groups) {
      width: 100%;
    }
  }

  :global(.product-card--quick-buy__container, div.tray--contained) {
    @include breakpoint(md) {
      overflow-y: auto;
    }
  }

  .back-in-stock-wrapper {
    padding: 24px 24px 16px;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

<div class="product-card">
  <span class="sr-only">{title}</span>
  <div
    class={`product-card--img-wrapper ${
      imgBorder && "product-card--img-wrapper__border"
    }`}
  >
    <a
      class="product-card--anchor"
      href={linkTo(url)}
      on:click={closeSearch}
      aria-label={`View ${title} product details`}
    >
      {#if badge}
        <div class="product-card--badge">
          <Badge label={badge} />
        </div>
      {/if}
      <div class="product-card--img" class:tray-open={isOpen || showTray}>
        {#if image && image.originalSrc}
          <Picture
            smImage={image.originalSrc}
            mdImage={image.originalSrc}
            altText={image.altText}
            {imageConfig}
            cover
            shopifyImage
          />
        {/if}
      </div>
    </a>

    {#if quickBuy && (isMobile || smallThumb) && productOptions && productVariants}
      <div class="product-card--quick-buy__container">
        <button
          class="product-card--quick-buy__button"
          on:click={() => {
            modal.open();
            isOpen = true;
            resetQuickBuy();
          }}
        >
          <Icon icon="local_mall" iconStyle="outline" switchable={true} />
        </button>
      </div>
    {/if}

    {#if quickBuy && !isMobile && !smallThumb && productOptions && productVariants}
      <div class="product-card--quick-buy__container">
        <button
          class="product-card--quick-buy__button"
          on:click|preventDefault={() => {
            if (!showTray) {
              openTray.ids = [...openTray.ids, `${id}-${index}`];
              openTray.route = $page?.url?.pathname;
              resetQuickBuy();
            } else {
              openTray.ids = openTray.ids?.filter(o => o !== `${id}-${index}`);
              openTray.route = $page?.url?.pathname;
            }
          }}
        >
          <Icon
            icon={showTray ? "close" : "local_mall"}
            iconStyle="outline"
            switchable={true}
          />
        </button>

        {#if showTray}
          {#if backInStock}
            <Tray desktopContained={true}>
              <div class="back-in-stock-wrapper">
                <BackInStock
                  variantId={selectedVariant?.node?.id}
                  closeButton
                  on:close={() => {
                    backInStock = false;
                  }}
                />
              </div>
            </Tray>
          {:else if giftCard}
            <Tray desktopContained={true}>
              <div class="back-in-stock-wrapper">
                <GiftCard
                  closeButton
                  on:close={() => {
                    giftCard = false;
                  }}
                  on:addToBasket={() => {
                    onAddToBasket(
                      selectedVariant?.node,
                      giftCardEmail,
                      giftCardName,
                      giftCardMessage,
                      giftCardDate,
                      giftCardSendOnDate,
                    );
                  }}
                  bind:email={giftCardEmail}
                  bind:name={giftCardName}
                  bind:message={giftCardMessage}
                  bind:date={giftCardDate}
                  bind:sendOnDate={giftCardSendOnDate}
                />
              </div>
            </Tray>
          {:else}
            <Tray desktopContained={true}>
              <div class="tray-wrapper">
                <ProductOptionSelector
                  {isGiftCard}
                  {sizeOptions}
                  {legSizeOptions}
                  {waistSizeOptions}
                  {trouserSizeOptions}
                  {url}
                  {productOptions}
                  hideViewDetails={true}
                  productImage={null}
                  {selectedOptions}
                  selectedLegOption={selectedLegSize}
                  selectedWaistOption={selectedWaistSize}
                  selectedSizeOption={selectedSize}
                  on:selectWaistSizeOption={event => {
                    waistOptionSelector(event.detail.value, event.detail.index);
                  }}
                  on:selectLegSizeOption={event => {
                    legOptionSelector(event.detail.value, event.detail.index);
                    if (!selectedWaistSize) {
                      trouserSizeError = $_("trouserSizeError.waist");
                    } else {
                      legOptionSelector(event.detail.value, event.detail.index);
                    }
                  }}
                  on:selectSizeOption={event => {
                    selectedSize = event.detail;
                    trouserSizeError = null;
                    optionSelector(event.detail.value, event.detail.index);
                  }}
                  on:selectVariant={event => {
                    optionSelector(event.detail, event.detail.index);
                  }}
                  on:openSizeGuide={() => {
                    modals.setSizeGuideDetails({
                      ...sizeGuide,
                      size: selectedWaistSize
                        ? selectedWaistSize
                        : selectedSize
                          ? typeof selectedSize === "string"
                            ? selectedSize
                            : selectedSize?.value
                          : undefined,
                    });
                    modals.open("sizeGuide");
                  }}
                />
                <div class="tray-wrapper--add-to-bag">
                  <AddToBag
                    label={selectedSize
                      ? selectedVariantInStock
                        ? isGiftCard
                          ? $_("Add recipient")
                          : $_("Add to Bag")
                        : $_("Notify me")
                      : isGiftCard
                        ? $_("Select Denomination")
                        : $_("Select size")}
                    disabled={selectedSize ? false : true}
                    on:click={() => {
                      onAddToBasket(selectedVariant.node);
                    }}
                  />
                </div>
              </div>
            </Tray>
          {/if}
        {/if}
      </div>
    {/if}
  </div>

  {#if title || subtitle || priceRange}
    <div
      class={`product-card--info ${
        paddingLg && "product-card--info__padded-lg"
      }`}
    >
      <a class="product-card--anchor" href={linkTo(url)} on:click={closeSearch}>
        <div class="product-card--info__base">
          <div class="product-card--info__title">
            <Typography component="span" variant={"button1"}
              >{title.toLowerCase() || ""}</Typography
            >
          </div>
          <div class="product-card--info__subtitle">
            <Typography component="span" variant="body2" theme="on-surface"
              >{subtitle || ""}</Typography
            >
          </div>
          {#if showPrice}
            {#if priceRange && priceRange.minVariantPrice && priceRange.minVariantPrice.amount}
              <div class="product-card--info__price">
                <Price
                  variantPrice={price}
                  {priceRange}
                  variantComparePrice={comparePrice}
                  {comparePriceRange}
                  variant="body2"
                />
              </div>
            {/if}
          {/if}
        </div>
      </a>
      {#if colourOptions && !isGiftCard}
        <div class="quick-buy-colours">
          <ScrollGradient gradientHeight="28px">
            <ColourSwatch
              {colourOptions}
              {selectedColourOption}
              on:select={event => {
                if (event.detail) {
                  openTray.ids = [];
                  optionSelector(event.detail.identifier, 1);
                  dispatch("selectColourOption", event.detail);
                  resetQuickBuy();
                }
              }}
            />
          </ScrollGradient>
        </div>
      {/if}
    </div>
  {/if}
</div>

{#if quickBuy && (isMobile || smallThumb)}
  <Modal
    bind:this={modal}
    backgroundColor="var(--on-surface-30)"
    {isOpen}
    usePortal={true}
    lockOnOpen={true}
    on:close={() => {
      isOpen = false;
    }}
  >
    {#if outOfStock === true}
      {#if selectedVariant && !selectedVariantInStock}
        <Tray>
          <BackInStock
            variantId={selectedVariant?.node?.id}
            withPadding
            closeButton
            on:close={() => {
              modal.close();
              resetQuickBuy();
            }}
          />
        </Tray>
      {/if}
    {:else if giftCard}
      <Tray>
        <GiftCard
          closeButton
          on:close={() => {
            giftCard = false;
          }}
          on:addToBasket={() => {
            onAddToBasket(
              selectedVariant.node,
              giftCardEmail,
              giftCardName,
              giftCardMessage,
              giftCardDate,
              giftCardSendOnDate,
            );
          }}
          withPadding
          bind:email={giftCardEmail}
          bind:name={giftCardName}
          bind:message={giftCardMessage}
          bind:date={giftCardDate}
          bind:sendOnDate={giftCardSendOnDate}
        />
      </Tray>
    {:else}
      <Tray>
        <ProductOptionSelector
          {isGiftCard}
          {url}
          title={title && title.toLocaleLowerCase()}
          subtitle=""
          {badge}
          {sizeOptions}
          {legSizeOptions}
          {waistSizeOptions}
          {trouserSizeOptions}
          {productOptions}
          {productVariants}
          {selectedColourOption}
          {price}
          {priceRange}
          {comparePrice}
          {comparePriceRange}
          productImage={imageObject}
          {selectedOptions}
          selectedSizeOption={selectedSize}
          selectedLegOption={selectedLegSize}
          selectedWaistOption={selectedWaistSize}
          {trouserSizeError}
          on:viewDetails={() => {
            closeSearch();
            modal.close();
          }}
          on:selectColourOption={event => {
            if (event.detail) {
              resetQuickBuy();
              optionSelector(event.detail.identifier, 1);
              dispatch("selectColourOption", event.detail);
            }
          }}
          on:selectWaistSizeOption={event => {
            waistOptionSelector(event.detail.value, event.detail.index);
          }}
          on:selectLegSizeOption={event => {
            legOptionSelector(event.detail.value, event.detail.index);
            if (!selectedWaistSize) {
              trouserSizeError = $_("trouserSizeError.waist");
            } else {
              legOptionSelector(event.detail.value, event.detail.index);
            }
          }}
          on:selectSizeOption={event => {
            selectedSize = event.detail;
            trouserSizeError = null;
            optionSelector(event.detail.value, event.detail.index);
          }}
          on:selectVariant={event => {
            optionSelector(event.detail, event.detail.index);
          }}
          on:openSizeGuide={() => {
            modals.setSizeGuideDetails({
              ...sizeGuide,
              size: selectedWaistSize
                ? selectedWaistSize
                : selectedSize
                  ? typeof selectedSize === "string"
                    ? selectedSize
                    : selectedSize?.value
                  : undefined,
            });
            modals.open("sizeGuide");
          }}
        />
        {#if !outOfStock}
          <div class="product-card--add-to-bag" in:fade={{ duration: 150 }}>
            {#if !selectedVariant}
              <AddToBag label={$_("Select size")} disabled />
            {:else if selectedVariant && selectedVariantInStock}
              <AddToBag
                label={selectedSize
                  ? selectedVariantInStock
                    ? isGiftCard
                      ? $_("Add recipient")
                      : $_("Add to Bag")
                    : $_("Notify me")
                  : isGiftCard
                    ? $_("Select Denomination")
                    : $_("Select size")}
                on:click={() => {
                  onAddToBasket(selectedVariant.node);
                }}
              />
            {:else if selectedVariant && !selectedVariantInStock}
              <Button
                primary
                fullWidth
                label={$_("Notify me")}
                on:click={() => {
                  outOfStock = true;
                }}
              />
            {/if}
          </div>
        {/if}
      </Tray>
    {/if}
  </Modal>
{/if}
